/** Pure helpers for the inbox meeting card (components/InviteCard.tsx). */
import type { QueryClient } from "@tanstack/react-query";
import type { CalendarInvite, MessageInviteState } from "./api";

/** First conferencing link in the invite (Teams, Zoom, Meet, Webex, our own). */
export function findJoinUrl(invite: Pick<CalendarInvite, "location" | "description">): string | null {
    const haystack = `${invite.location ?? ""}\n${invite.description ?? ""}`;
    const urls = haystack.match(/https?:\/\/[^\s<>"')\]]+/g) ?? [];
    const conferencing = /teams\.microsoft\.com\/(meet|l\/meetup-join)|zoom\.us\/(j|my|w)\/|meet\.google\.com\/|webex\.com\/(meet|join|[a-z0-9-]+\/j\.php)|meet\.kardoe\.com|whereby\.com|gotomeeting\.com\/join|chime\.aws\//i;
    return urls.find(url => conferencing.test(url)) ?? null;
}

export function localZone(): string {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; }
}

export function fmt(iso: string, timeZone: string, opts: Intl.DateTimeFormatOptions): string {
    try { return new Intl.DateTimeFormat(undefined, { timeZone, ...opts }).format(new Date(iso)); }
    catch { return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso)); }
}

/** "Mon, Nov 2 · 9:30 – 10:00 AM EST" in `timeZone`. */
export function formatRange(startIso: string, endIso: string | null, timeZone: string, allDay: boolean): string {
    const day = fmt(startIso, timeZone, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    if (allDay) return `${day} · All day`;
    const start = fmt(startIso, timeZone, { hour: "numeric", minute: "2-digit" });
    const end = endIso ? fmt(endIso, timeZone, { hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "";
    return end ? `${day} · ${start} – ${end}` : `${day} · ${start}`;
}


/** Cache key for one message's invite card; prefixed by UID so a meeting update reaches every copy. */
export const inviteQueryKey = (uid: string, messageId: string) => ["message-invite", uid, messageId] as const;

export type InviteUpdate = {
    uid: string;
    meeting: MessageInviteState["meeting"];
    attendees: MessageInviteState["attendees"];
};

/** Apply a `calendar-invite:updated` broadcast (or an RSVP response) to every card for that UID. */
export function patchInviteCaches(queryClient: QueryClient, update: InviteUpdate): void {
    queryClient.setQueriesData<MessageInviteState | { ok: false; error: string }>(
        { queryKey: ["message-invite", update.uid] },
        current => {
            if (!current || !current.ok) return current;
            const mine = current.myEmail
                ? update.attendees.find(a => a.email.toLowerCase() === current.myEmail)
                : undefined;
            return {
                ...current,
                meeting: update.meeting,
                attendees: update.attendees,
                myResponse: mine?.rsvpStatus ?? "needs_action",
                superseded: Boolean(update.meeting && current.invite.method === "REQUEST" && (
                    update.meeting.status === "cancelled"
                    || (update.meeting.sequence ?? 0) > (current.invite.sequence ?? 0)
                )),
            };
        },
    );
}
